// Fiche du job : documents pour l'atelier (BAT, maquette, photo du produit fini).
import { useCallback, useEffect, useState, type Dispatch, type DragEvent, type SetStateAction } from "react";
import { DOC_KIND_LABEL, type DocKind, type DocPage, type JobDocument } from "../data/types.ts";
import { DocError, MAX_PAGES, fileToPages, guessKind } from "../lib/docs.ts";
import { DocViewer } from "../ui/DocViewer.tsx";
import { attempt, toast, useApp } from "../ui/kit.tsx";

/** Document préparé avant la création du job : envoyé juste après. */
export interface PendingDoc { id: string; name: string; kind: DocKind; thumb: string; pages: DocPage[] }

export function JobDocs({ jobId, edit, pending, setPending }: {
  jobId: string | null; edit: boolean; pending: PendingDoc[]; setPending: Dispatch<SetStateAction<PendingDoc[]>>;
}) {
  const { api } = useApp();
  const [docs, setDocs] = useState<JobDocument[]>([]);
  const [busy, setBusy] = useState("");
  const [over, setOver] = useState(false);
  const [view, setView] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!jobId) { setDocs([]); return; }
    try { setDocs(await api.documents(jobId)); } catch { /* affiché vide */ }
  }, [api, jobId]);
  useEffect(() => { reload(); }, [reload]);

  const addFiles = async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      try {
        setBusy(`Préparation de « ${file.name} »…`);
        const { pages, thumb, truncated } = await fileToPages(file, (done, total) => setBusy(`Préparation de « ${file.name} » : page ${Math.min(done + 1, total)} / ${total}…`));
        if (truncated) toast(`Seules les ${MAX_PAGES} premières pages de « ${file.name} » sont gardées.`, "info");
        const kind = guessKind(file.name, file.type);
        if (jobId) {
          setBusy(`Envoi de « ${file.name} »…`);
          const ok = await attempt(() => api.addDocument(jobId, { name: file.name, kind, thumb, pages }), `« ${file.name} » ajouté : visible sur la tablette`);
          if (ok) await reload();
        } else {
          setPending((cur) => [...cur, { id: crypto.randomUUID(), name: file.name, kind, thumb, pages }]);
        }
      } catch (e) {
        console.error("Document", file.name, e);
        toast(e instanceof DocError ? e.message : `« ${file.name} » n'a pas pu être préparé.`, "err");
      }
    }
    setBusy("");
  };

  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); if (edit && !busy && e.dataTransfer.files.length) addFiles(e.dataTransfer.files); };

  const shown: JobDocument[] = [
    ...docs,
    ...pending.map((p) => ({ id: p.id, job_id: "", name: p.name, kind: p.kind, pages: p.pages.length, thumb: p.thumb, created_at: "" })),
  ];
  const isPending = (id: string) => pending.some((p) => p.id === id);
  const loadPage = async (id: string, page: number) => {
    const p = pending.find((x) => x.id === id);
    return p ? p.pages[page - 1].data : api.documentPage(id, page);
  };
  const setKind = async (d: JobDocument, kind: DocKind) => {
    if (isPending(d.id)) setPending((cur) => cur.map((p) => (p.id === d.id ? { ...p, kind } : p)));
    else { await attempt(() => api.updateDocument(d.id, { kind })); reload(); }
  };
  const remove = async (d: JobDocument) => {
    if (isPending(d.id)) { setPending((cur) => cur.filter((p) => p.id !== d.id)); return; }
    if (!confirm(`Retirer « ${d.name} » ? La tablette ne l'affichera plus.`)) return;
    await attempt(() => api.deleteDocument(d.id), "Document retiré");
    reload();
  };

  return (
    <section className="card form-card">
      <h2 className="form-sec">Documents pour l'atelier</h2>
      <p className="muted small form-sec-intro">BAT, maquette ou photo du produit fini : le conducteur les ouvre en grand sur la tablette de son poste.</p>
      {shown.length > 0 && (
        <ul className="doc-list">
          {shown.map((d) => (
            <li key={d.id}>
              <button type="button" className="doc-thumb" onClick={() => setView(d.id)} aria-label={`Voir ${d.name}`}><img src={d.thumb} alt="" /></button>
              <div className="doc-info">
                <b>{d.name}</b>
                <span className="muted small">{d.pages > 1 ? `${d.pages} pages` : "1 page"}{isPending(d.id) ? " · envoyé à la création du job" : ""}</span>
              </div>
              {edit ? (
                <select className="inline-select" value={d.kind} onChange={(e) => setKind(d, e.target.value as DocKind)} aria-label="Type de document">
                  {(Object.keys(DOC_KIND_LABEL) as DocKind[]).map((k) => <option key={k} value={k}>{DOC_KIND_LABEL[k]}</option>)}
                </select>
              ) : <span className="badge">{DOC_KIND_LABEL[d.kind]}</span>}
              <button type="button" className="btn btn-small" onClick={() => setView(d.id)}>Voir</button>
              {edit && <button type="button" className="icon-btn" onClick={() => remove(d)} aria-label={`Retirer ${d.name}`}>✕</button>}
            </li>
          ))}
        </ul>
      )}
      {edit && (
        <label className={`doc-drop ${over ? "over" : ""} ${busy ? "busy" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
          <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,.pdf" multiple disabled={!!busy}
            onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
          {busy ? <b>{busy}</b> : <><b>Ajouter un BAT, une maquette ou une photo</b><span>PDF, JPEG ou PNG. Glissez le fichier ici ou cliquez pour le choisir.</span></>}
        </label>
      )}
      {!edit && !shown.length && <p className="muted small">Aucun document.</p>}
      {view && <DocViewer docs={shown} startDoc={view} loadPage={loadPage} onClose={() => setView(null)} />}
    </section>
  );
}
